import * as React from 'react'
import { Link, } from 'react-router-dom'

import {
  OpenPopover,
  SkillGroup,
  SkillGroupSummary,
  StudentResult,
} from './interfaces'
import {
  accountCommentIcon,
  closeIcon,
  brightGreenTriangleUpIcon,
  noDataYet,
  proficiencyTextToTag,
  accountIcon,
} from './shared'
import SkillGroupTooltip from './skillGroupTooltip'

import useModalAccessibility from '../../../../../Shared/hooks/useModalAccessibility'
import useStickyTableHeader from '../../../../../Shared/hooks/useStickyTableHeader'
import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths'
import {
  helpIcon,
  Tooltip,
  DataTable,
  Spinner,
  DataTableChip,
} from '../../../../../Shared/index'

const POST_TEST_DESCRIPTION = '<br/><br/>The number of students who improved or maintained this skill shows you how many students had a positive outcome for this skill area on the post-diagnostic. Students who \"improved\" the skill showed growth by answering more questions for this skill correctly on the post-diagnostic than they did on the pre. Students who \"maintained\" the skill showed proficiency by answering all questions for this skill correctly on both the pre and the post-diagnostic.<br/><br/>The up arrow and number following it focuses just on growth between diagnostics. It shows you how many students improved in the skill from pre to post-diagnostic, not including students who had already shown proficiency in the skill on the pre-diagnostic.'

const TABLE_WIDTH_PROPS = {
  maxTableWidth: 1200,
  minTableWidth: 963
}

interface StudentResultsTableProps {
  skillGroupSummaries: SkillGroupSummary[];
  studentResults: StudentResult[];
  openPopover: OpenPopover;
  setOpenPopover: (popover: OpenPopover) => void;
  responsesLink: (id: number) => string;
  isPreTest?: boolean;
}

interface PopoverProps {
  studentResult: StudentResult;
  skillGroup: SkillGroup;
  skillGroupSummary: SkillGroupSummary;
  closePopover: () => void;
  responsesLink: (id: number) => string;
}

interface StudentResultCellProps {
  studentResult: StudentResult;
  skillGroup: SkillGroup;
  skillGroupSummary: SkillGroupSummary;
  setOpenPopover: (popover: OpenPopover) => void;
  openPopover: OpenPopover;
  responsesLink: (id: number) => string;
}

function renderDelta(count: number) {
  return (
    <div className="skills-delta">
      {count ? brightGreenTriangleUpIcon : null}
      <span className="skill-count">{count ? count : null}</span>
    </div>
  )
}

function renderStudentHeaderSkillsData({ isPreTest, totalAcquiredSkillGroupsCount, correct_question_text, total_acquired_skill_groups_count, total_maintained_skill_group_proficiency_count, correct_skill_groups_text }) {
  const diagnosticNotCompletedMessage = <span className="name-section-subheader">Diagnostic not completed</span>
  const tooltipText = "The number of improved skills helps you celebrate each student's learning and growth. It shows you how many skills the student improved between the pre and post-diagnostic. A skill is considered “improved” if the student answered more questions for that skill correctly on the post-diagnostic than they did on the pre. <br/><br/> If the student maintained skills, you'll see that number in parentheses. A skill is considered “maintained” if the student answered all the questions for that skill correctly on both the pre and the post-diagnostic."
  const skillsDelta = (<div className="skills-delta">
    <Tooltip
      tooltipText={tooltipText}
      tooltipTriggerText={<img alt={helpIcon.alt} src={helpIcon.src} />}
    />
  </div>)
  const skillsSubHeader = correct_question_text ? <div className="name-section-subheader"><span className="correct-skill-text">{correct_question_text}</span>{skillsDelta}</div> : diagnosticNotCompletedMessage
  const noSkillChange = !total_acquired_skill_groups_count && !total_maintained_skill_group_proficiency_count

  let skillGroupsSubHeader

  if (!isPreTest && !noSkillChange) {
    const acquiredText = totalAcquiredSkillGroupsCount === 1 ? '1 Improved Skill' : `${totalAcquiredSkillGroupsCount} Improved Skills`
    const maintainedText = total_maintained_skill_group_proficiency_count === 1 ? '(1 Maintained)' : `(${total_maintained_skill_group_proficiency_count} Maintained)`
    skillGroupsSubHeader = (
      <div className="name-section-subheader">
        {!!total_acquired_skill_groups_count && <div className="skills-delta">{brightGreenTriangleUpIcon}</div>}
        {!!total_acquired_skill_groups_count && <span className="correct-skill-text acquired">{acquiredText}</span>}
        {!!total_maintained_skill_group_proficiency_count && <span className="correct-skill-text maintained">{maintainedText}</span>}
      </div>
    )
  }
  return (
    <React.Fragment>
      {skillGroupsSubHeader}
      {skillsSubHeader}
    </React.Fragment>
  )
}

const Popover = ({
  closePopover,
  responsesLink,
  skillGroup,
  skillGroupSummary,
  studentResult,
}: PopoverProps) => {
  const { modalRef } = useModalAccessibility(closePopover);

  return (
    <div
      aria-label={`${studentResult.name} results for ${skillGroup.skill_group}`}
      aria-modal={true}
      className="student-results-popover-container"
      id={`popover-${studentResult.id}-${skillGroup.id}`}
      ref={modalRef}
      role="dialog"
      tabIndex={-1}
    >
      <section className="student-results-popover">
        <header>
          <h2>{skillGroup.skill_group}</h2>
          <button
            aria-label="Close popover"
            className="interactive-wrapper focus-on-light"
            onClick={closePopover}
            type="button"
          >
            {closeIcon}
          </button>
        </header>
        <p dangerouslySetInnerHTML={{ __html: skillGroupSummary.description }} />
        <Link
          className="focus-on-light"
          to={`${responsesLink(studentResult.id)}#${skillGroup.id}`}
        >
          {accountCommentIcon}
          <span>
            View {studentResult.name}&#39;s responses for {skillGroup.skill_group}
          </span>
        </Link>
      </section>
    </div>
  );
};

const StudentResultCell = ({
  openPopover,
  responsesLink,
  setOpenPopover,
  skillGroup,
  skillGroupSummary,
  studentResult,
}: StudentResultCellProps) => {
  const { id, number_of_correct_questions_text, proficiency_text } = skillGroup;

  function closePopover() {
    setOpenPopover({});
  }

  function showPopover() {
    setOpenPopover({
      studentId: studentResult.id,
      skillGroupId: id,
    });
  }

  const popoverId = `popover-${studentResult.id}-${skillGroup.id}`;

  return (
    <div className="student-result-cell">
      <button
        aria-controls={popoverId}
        aria-expanded={openPopover.studentId === studentResult.id && openPopover.skillGroupId === id}
        aria-haspopup="dialog"
        className="interactive-wrapper focus-on-light"
        onClick={showPopover}
        type="button"
      >
        {proficiencyTextToTag[proficiency_text]}
        <div className="correct-skills-and-delta-wrapper">
          <span className="number-of-correct-skills-text">
            {number_of_correct_questions_text}
          </span>
        </div>
      </button>
      {openPopover.studentId === studentResult.id && openPopover.skillGroupId === id && (
        <Popover
          closePopover={closePopover}
          responsesLink={responsesLink}
          skillGroup={skillGroup}
          skillGroupSummary={skillGroupSummary}
          studentResult={studentResult}
        />
      )}
    </div>
  );
};


const StudentResultsTable = ({ isPreTest, skillGroupSummaries, studentResults, openPopover, setOpenPopover, responsesLink }: StudentResultsTableProps) => {
  const containerRef = React.useRef<HTMLElement>(null);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);

  const completedStudentResults = studentResults.filter(sr => sr.skill_groups)
  const completedStudentCount = completedStudentResults.length

  // Calculate the remaining width for the name column at max table width
  // This ensures the name column absorbs extra space while skill columns stay at 185px
  const skillColumnsWidth = skillGroupSummaries.length * 185;
  const nameColumnBaseWidth = TABLE_WIDTH_PROPS.maxTableWidth - skillColumnsWidth;

  const columnWidths = useScaledTableColumnWidths({
    columnWidths: [
      { width: nameColumnBaseWidth, minWidth: 195 },
      ...skillGroupSummaries.map(() => ({ width: 185, minWidth: 185 }))
    ],
    containerRef,
    additionalWidthRestrictions: 18, // Account for padding/border on each side of rows
    ...TABLE_WIDTH_PROPS,
  });

  function renderCountData({ completedStudentCount, proficientStudentCount }) {
    if (!completedStudentCount) { return }

    const proficiencyText = `${proficientStudentCount} of ${completedStudentCount} student${completedStudentCount === 1 ? '' : 's'} proficient`

    if (isPreTest) {
      return <span className="label">{proficiencyText}</span>
    }

    return (
      <div className="student-proficiency-data-container">
        <span className="label">{proficiencyText}</span>
        {renderDelta(proficientStudentCount)}
      </div>
    )
  }

  const headers = [
    {
      attribute: 'name',
      name: 'Student Name',
      renderCellsAsHeaders: true,
      rowSectionClassName: 'name-cell border-right',
      width: columnWidths[`${nameColumnBaseWidth}w-195min`],
      noTooltip: true
    },
    ...skillGroupSummaries.map((skillGroupSummary, index) => {
      const { name, description, gained_proficiency_in_post_test_student_names, not_yet_proficient_student_names } = skillGroupSummary
      const appendedDescription = isPreTest ? description : description + POST_TEST_DESCRIPTION
      const notYetProficientStudentCount = isPreTest && not_yet_proficient_student_names.length
      const proficientStudentCount = isPreTest ? completedStudentCount - notYetProficientStudentCount : gained_proficiency_in_post_test_student_names.length

      return {
        ariaLabel: name,
        attribute: `skillGroup${index}`,
        name: (
          <div>
            <div className="name-and-tooltip">
              <span className="skill-name">{name}</span>
              <SkillGroupTooltip description={appendedDescription} key={name} name={name} />
            </div>
            {renderCountData({ completedStudentCount, proficientStudentCount })}
          </div>
        ),
        headerClassName: 'skill-group-header',
        width: columnWidths['185w-185min'],
      }
    })
  ];


  useStickyTableHeader({
    wrapperRef: tableWrapperRef,
    tableRef: tableRef,
    enabled: !!completedStudentCount,
    dependencies: [Object.values(columnWidths).join('|')],
  });

  if (!studentResults) {
    return (
      <div className="student-results-table-container">
        <Spinner />
      </div>
    )
  }

  if (!completedStudentCount) {
    return (
      <div className="student-results-table-container">
        <div className="no-data-yet">{noDataYet}</div>
      </div>
    )
  }

  const rows = () => {
    return studentResults.map(studentResult => {
      const { name, skill_groups, id, total_acquired_skill_groups_count, correct_question_text, correct_skill_groups_text, total_maintained_skill_group_proficiency_count } = studentResult

      if (!skill_groups) {
        return {
          id: id || name,
          name: (
            <div>
              <DataTableChip color="grey" icon={accountIcon} label={name} />
              <span className="name-section-subheader">Diagnostic not completed</span>
            </div>
          ),
          ...skillGroupSummaries.reduce((acc, _, index) => ({ ...acc, [`skillGroup${index}`]: '' }), {})
        }
      }

      const totalAcquiredSkillGroupsCount = total_acquired_skill_groups_count > 0 ? total_acquired_skill_groups_count : 0;
      const studentHeaderSkillsData = renderStudentHeaderSkillsData({
        isPreTest,
        totalAcquiredSkillGroupsCount,
        correct_question_text,
        total_acquired_skill_groups_count,
        total_maintained_skill_group_proficiency_count,
        correct_skill_groups_text
      });

      const skillGroupCells = skillGroupSummaries.reduce((acc, skillGroupSummary, index) => {
        const skillGroup = skill_groups.find(sg => sg.skill_group === skillGroupSummary.name)
        if (skillGroup) {
          acc[`skillGroup${index}`] = (
            <StudentResultCell
              key={`${id}-${skillGroup.skill_group}`}
              openPopover={openPopover}
              responsesLink={responsesLink}
              setOpenPopover={setOpenPopover}
              skillGroup={skillGroup}
              skillGroupSummary={skillGroupSummary}
              studentResult={studentResult}
            />
          )
        } else {
          acc[`skillGroup${index}`] = ''
        }
        return acc
      }, {})

      return {
        id: id || name,
        name: (
          <div>
            <DataTableChip color="grey" icon={accountIcon} label={name} />
            {studentHeaderSkillsData}
          </div>
        ),
        ...skillGroupCells
      }
    })
  }

  return (
    <section className="student-results-table-container" ref={containerRef}>
      <div className="data-table-wrapper" ref={tableWrapperRef}>
        <DataTable
          headers={headers}
          reportingFormat={true}
          rows={rows()}
          tableRef={tableRef}
        />
      </div>
    </section>
  )
}

export default StudentResultsTable
